import clsx from "clsx";
import React, {
  memo,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
  useTransition,
} from "react";
import { MdExpandMore } from "react-icons/md";
import Badge, { EBadgeKind } from "../Badge/Badge";
import { motion, useReducedMotion } from "framer-motion";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import SlideDownUpAnimation from "../Animations/SlideDownUpAnimation";
import { forceIconSize } from "src/helpers/icon-utils";
import { ICON_SIZES } from "src/constants/icons";
import { IButton } from "../Button/Button.types";

type CollapsibleBlockType = {
  title: string;
  children: ReactElement;
  open?: boolean;
  badge?: string;
  uppercase?: boolean;
  buttons?: Array<IButton>;
  onChange?: (isOpen: boolean) => void;
  icon?: ReactElement;
  badgeKind?: EBadgeKind;
  blocked?: boolean;
  keepAlive?: boolean;
  id?: string;
  buttonsAlign?: "left" | "center" | "right";
  hasError?: boolean;
  buttonsNote?: ReactNode;
  showHeader?: boolean;
};

const CollapsibleBlock: React.FC<CollapsibleBlockType> = ({
  title,
  children,
  open = true,
  badge = null,
  uppercase = false,
  buttons = [],
  icon,
  onChange = null,
  badgeKind = EBadgeKind.INFO,
  blocked = true,
  keepAlive = false,
  id,
  buttonsAlign = "right",
  hasError = false,
  buttonsNote = null,
  showHeader = true,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState<boolean>(blocked ? true : open);
  const [isPending, startTransition] = useTransition();
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const [shakeBadge, setShakeBadge] = useState<boolean>(false);
  const [floatingIcon, setFloatingIcon] = useState<boolean>(false);

  useEffect(() => {
    // if (!blocked) {
    setIsOpen(open);
    // }
  }, [open]);

  // useEffect(() => {
  //   setShowButtons(isOpen);
  // }, [isOpen]);

  useEffect(() => {
    setShowButtons(open);
  }, [open]);

  return (
    <div className={clsx(["flex", "flex-col", "w-full"])}>
      {showHeader && (
        <motion.div
          className={clsx([
            "flex",
            "flex-row",
            "justify-between",
            "items-center",
            !blocked && ["cursor-pointer"],
            isOpen && ["border-blue-100", "dark:border-cyan-800"],
            !isOpen && ["border-gray-100", "dark:border-gray-600"],
            "border-b",
            "border-dashed",
            "pb-1",
            "gap-2",
          ])}
          onClick={() => {
            if (blocked) return false;
            if (onChange) {
              startTransition(() => onChange?.(!isOpen));
            } else {
              setIsOpen(!isOpen);
            }
          }}
          whileHover={blocked ? {} : { opacity: 0.7 }}
          whileTap={blocked ? {} : { opacity: 0.7 }}
          onHoverStart={() => {
            if (blocked) return false;
            setFloatingIcon(true);
          }}
          onHoverEnd={() => {
            if (blocked) return false;

            setShakeBadge(false);
            setFloatingIcon(false);
          }}
        >
          <motion.div
            className={clsx([
              "flex",
              "flex-shrink",
              "flex-grow",
              "font-medium",
              "text-xs",
              "md:text-sm",
              "flex-row",
              "gap-1",
              "items-center",
              "truncate",
              isOpen && ["text-blue-900", "dark:text-cyan-400"],
            ])}
          >
            <div
              className={clsx(["gap-1", "_inline-flex", "hidden", "md:hidden"])}
            >
              <div
                className={clsx([
                  "inline-flex",
                  "w-[4px]",
                  "h-[10px]",
                  "bg-blue-50",
                  "dark:bg-gray-700",
                ])}
              ></div>
              <div
                className={clsx([
                  "inline-flex",
                  "w-[4px]",
                  "h-[10px]",
                  "bg-blue-50",
                  "dark:bg-gray-700",
                ])}
              ></div>
            </div>
            {icon &&
              forceIconSize({
                icon,
                size: ICON_SIZES.SM,
              })}
            <div
              className={clsx([
                "truncate",
                "block",
                hasError && ["text-red-500", "dark:text-teal-400"],
              ])}
            >
              {title}
            </div>
          </motion.div>

          <div
            className={clsx([
              "flex",
              "items-center",
              "flex-row",
              "flex-shrink-0",
            ])}
          >
            {badge && (
              <motion.div
                key={shakeBadge ? "shaking" : "idle"}
                {...(!prefersReducedMotion && {
                  animate: shakeBadge ? { x: [0, -2, 2, -2, 2, 0] } : {},
                  transition: {
                    duration: 0.4,
                    ease: "easeInOut",
                  },
                })}
              >
                <Badge kind={badgeKind} text={badge} />
              </motion.div>
            )}
            {!blocked && (
              <motion.div
                {...(!prefersReducedMotion && {
                  initial: {
                    rotate: isOpen ? 180 : 0,
                  },
                  animate: {
                    rotate: isOpen ? 180 : 0,
                    y: floatingIcon ? [0, -1.5, 1.5, -1.5, 1.5, 0] : 0,
                  },
                  transition: {
                    rotate: { duration: 0.2 },
                    y: floatingIcon
                      ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
                      : {},
                  },
                })}
              >
                <MdExpandMore
                  size={ICON_SIZES.MD}
                  className={clsx([
                    !isOpen && ["opacity-50"],
                    isOpen && ["text-blue-400", "dark:text-cyan-400"],
                    hasError && ["!text-red-500", "dark:!text-teal-400"],
                  ])}
                />
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      <SlideDownUpAnimation show={isOpen} keepAlive={keepAlive}>
        <div
          className={clsx([
            "flex",
            "w-full",
            "flex-col",
            "pb-[2px]",
            {
              "pt-4": showHeader,
            },
          ])}
        >
          {children}
        </div>
      </SlideDownUpAnimation>

      {!!((buttons?.length || buttonsNote) && showButtons) && (
        <motion.div
          {...(!prefersReducedMotion && {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
            transition: { duration: 0.2 },
          })}
          className="flex w-full pt-2 items-center"
        >
          {buttonsNote && (
            <div
              className={clsx(["flex-shrink", "flex", "text-xs", "flex-grow"])}
            >
              {buttonsNote}
            </div>
          )}

          {buttons?.length > 0 && (
            <div className={clsx(["flex", "flex-grow", "flex-shrink-0"])}>
              <ButtonGroup
                buttons={buttons}
                align={buttonsAlign}
                gap={1}
                fullWidth
              />
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default memo(CollapsibleBlock);
